import { z } from 'zod';

const emailSchema = z.string().email();
const phoneSchema = z.string().regex(/^\+[1-9]\d{6,14}$/, 'invalid phone');

// Lenient at the form layer: backend is the authority on what an identity is.
// In MVP `aidu.otp.allow-sms=false` — see spec §2.6 — only email is accepted for
// register/OTP/password-reset; login with password may use email or phone.
const identitySchema = z.string().trim().min(3, 'required');

// MVP email-only registration — phone is hidden in UI per §2.6.
// Keep phoneSchema available for the future when SMS is enabled.
export const registerSchema = z.object({
  email: z.string().trim().min(1, 'required').refine(
    (v) => emailSchema.safeParse(v).success,
    'invalid email',
  ),
  password: z
    .string()
    .min(8, 'min 8 characters')
    .optional()
    .or(z.literal('').transform(() => undefined)),
});

export type RegisterFormValues = z.infer<typeof registerSchema>;

export const verifySchema = z.object({
  code: z.string().regex(/^\d{6}$/, '6 digits required'),
});

export type VerifyFormValues = z.infer<typeof verifySchema>;

export const loginPasswordSchema = z.object({
  identity: identitySchema,
  password: z.string().min(1, 'required'),
});

export type LoginPasswordFormValues = z.infer<typeof loginPasswordSchema>;

export const otpRequestSchema = z.object({
  identity: identitySchema,
});

export type OtpRequestFormValues = z.infer<typeof otpRequestSchema>;

// MVP: only EMAIL channel is supported by backend (§2.6). When SMS is enabled,
// flip this back to identity-based detection.
export function detectChannel(_identity: string): 'EMAIL' | 'SMS' {
  return 'EMAIL';
}

export { phoneSchema };

// §4.4 — password reset.
export const passwordResetRequestSchema = z.object({
  identity: z.string().email('valid email required'),
});
export type PasswordResetRequestValues = z.infer<typeof passwordResetRequestSchema>;

export const passwordResetConfirmSchema = z
  .object({
    identity: z.string().email(),
    code: z.string().regex(/^\d{6}$/, '6 digits required'),
    newPassword: z.string().min(8, 'min 8 characters'),
    confirmPassword: z.string().min(1, 'required'),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'passwords do not match',
  });
export type PasswordResetConfirmValues = z.infer<typeof passwordResetConfirmSchema>;

// Change-password schema lives in entities/account — see entities/account/model/schemas.ts.
